'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { LoginModal } from '@/app/landing/LoginModal'
import {
  clearPostLoginRedirect,
  isSafeInternalPath,
  setPostLoginRedirect,
} from '@/lib/postLoginRedirect'
import { clearPendingPracticeQuestionAfterLogin, setPendingPracticeQuestionAfterLogin } from '@/lib/pendingPracticeQuestionLogin'
import {
  persistLandingLoginEntry,
  type LandingLoginEntry,
} from '@/lib/ga'

type LandingLoginFlow = 'none' | 'practice_question' | 'dashboard_only' | 'post_login_path'

type OpenLoginOptions = {
  /** After login, open this question on the dashboard practice section. */
  pendingQuestionId?: string
  /** After login, go to /dashboard (no specific question). */
  redirectToDashboard?: boolean
  /**
   * After login, navigate to this internal path (e.g. /dashboard?journey=jd).
   * Ignored if pendingQuestionId is set.
   */
  postLoginPath?: string
  /** GA: which landing surface opened the login modal. */
  loginEntry?: LandingLoginEntry
}

type LandingLoginContextValue = {
  openLoginModal: (options?: OpenLoginOptions) => void
}

const LandingLoginContext = createContext<LandingLoginContextValue | null>(null)

export function useLandingLogin(): LandingLoginContextValue {
  const ctx = useContext(LandingLoginContext)
  if (!ctx) {
    throw new Error('useLandingLogin must be used within LandingLoginProvider')
  }
  return ctx
}

export function LandingLoginProvider({ children }: { children: ReactNode }) {
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [loginEntry, setLoginEntry] = useState<LandingLoginEntry>('unknown')
  const flowRef = useRef<LandingLoginFlow>('none')

  const openLoginModal = useCallback((options?: OpenLoginOptions) => {
    const entry = options?.loginEntry ?? 'unknown'
    setLoginEntry(entry)
    persistLandingLoginEntry(entry)
    flowRef.current = 'none'
    if (options?.pendingQuestionId) {
      setPendingPracticeQuestionAfterLogin(options.pendingQuestionId)
      setPostLoginRedirect('/dashboard')
      flowRef.current = 'practice_question'
    } else if (
      options?.postLoginPath &&
      isSafeInternalPath(options.postLoginPath)
    ) {
      setPostLoginRedirect(options.postLoginPath.trim())
      flowRef.current = 'post_login_path'
    } else if (options?.redirectToDashboard) {
      setPostLoginRedirect('/dashboard')
      flowRef.current = 'dashboard_only'
    }
    setShowLoginModal(true)
  }, [])

  const closeLoginModal = useCallback(() => {
    setShowLoginModal(false)
    if (flowRef.current === 'practice_question') {
      clearPendingPracticeQuestionAfterLogin()
    }
    if (
      flowRef.current === 'practice_question' ||
      flowRef.current === 'dashboard_only' ||
      flowRef.current === 'post_login_path'
    ) {
      clearPostLoginRedirect()
    }
    flowRef.current = 'none'
  }, [])

  const value = useMemo(() => ({ openLoginModal }), [openLoginModal])

  // /invite flow sets open_register_tab before redirecting to / (sign-in page is disabled).
  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      if (sessionStorage.getItem('open_register_tab') === 'true') {
        sessionStorage.removeItem('open_register_tab')
        openLoginModal()
      }
    } catch {
      // ignore
    }
  }, [openLoginModal])

  return (
    <LandingLoginContext.Provider value={value}>
      {children}
      {showLoginModal && (
        <LoginModal
          open={showLoginModal}
          onClose={closeLoginModal}
          loginEntry={loginEntry}
        />
      )}
    </LandingLoginContext.Provider>
  )
}
